/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */
import humanizeDuration from 'humanize-duration'
import { isEmpty, isNull, isUndefined } from 'lodash'
import { DateTime } from 'luxon'

import { IsoDate } from '@/types/dashboard-types'

import { VSP_ACCESS_TYPE } from '../StatusColumns/StatusColumnsContext'
import { TemperatureUnit } from '../types'

export const stickyColumns = ['asset']

export const isDraggable = (id: string) => !stickyColumns.includes(id)
export const getStickyBorderClass = (id: string) => (id === 'asset' ? 'border-r border-gray-300' : '')

const bearings = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']

export const getBearingDirection = (bearingDegrees: number | null) => {
  if (typeof bearingDegrees === 'number' && bearingDegrees >= 0 && bearingDegrees <= 360) {
    const quadrant = Math.floor((bearingDegrees + 22.5) / 45)

    if (quadrant === 8) {
      return bearings[0]
    } else if (quadrant >= 0 && quadrant < 8) {
      return bearings[quadrant]
    }
  }

  return ''
}

export const timeSinceFromMilliseconds = (
  milliseconds: number,
  unitCount: number,
  ignoreSeconds: boolean = true
): string => {
  // TIP: to find current time in milliseconds, use:
  // var  current_time_milliseconds = new Date().getTime();

  function numberEnding(number: number) {
    return number > 1 ? 's' : ''
  }
  const stringParts = []

  let temp = Math.floor(milliseconds / 1000)
  const years = Math.floor(temp / 31536000)
  if (years) {
    stringParts.push(years + ' year' + numberEnding(years))
  }
  // TODO: Months! Maybe weeks?
  const days = Math.floor((temp %= 31536000) / 86400)
  if (days) {
    stringParts.push(days + ' day' + numberEnding(days))
  }
  const hours = Math.floor((temp %= 86400) / 3600)
  if (hours) {
    stringParts.push(hours + ' hour' + numberEnding(hours))
  }
  const minutes = Math.floor((temp %= 3600) / 60)
  if (minutes) {
    stringParts.push(minutes + ' minute' + numberEnding(minutes))
  }
  const seconds = temp % 60
  if (seconds && !ignoreSeconds) {
    stringParts.push(seconds + ' second' + numberEnding(seconds))
  }

  if (stringParts.length === 0) {
    stringParts.push('Less than a minute')
  }

  const filteredParts = unitCount ? stringParts.slice(0, unitCount) : stringParts
  return filteredParts.join(' ')
}

export const formatDate12 = (date: Date | string, utc: boolean = false): string => {
  const dateLuxon = utc ? DateTime.fromJSDate(new Date(date)).toUTC() : DateTime.fromJSDate(new Date(date))

  return dateLuxon.toFormat('dd-LLL-yyyy hh:mm a')
}

export const timeSinceFormatted = (sampleTime: Date | IsoDate) => {
  const delta = DateTime.now()
    .diff(DateTime.fromJSDate(new Date(sampleTime)))
    .toMillis()
  return delta > 0
    ? humanizeDuration(delta, {
        largest: 2,
        round: true,
        delimiter: ' and ',
      }) + ' ago'
    : ''
}

export const isNullOrUndefined = (value: unknown) => isNull(value) || isUndefined(value)

export const createTooltip = (prefix: string, sampleTime?: Date | undefined, suffix?: string): string => {
  let tooltip = prefix
  if (sampleTime) {
    tooltip += ` since ${formatDate12(sampleTime)}`
    const delta = timeSinceFormatted(sampleTime)
    if (delta) {
      tooltip += `\n${delta}`
    }
  }
  if (suffix) {
    tooltip += `\n${suffix}`
  }
  return tooltip
}

export const convertTemperature = (value: number, unit: TemperatureUnit): string => {
  if (typeof value !== 'number') {
    return ''
  }
  const convertedValue = unit === 'Imperial' ? (value * 9) / 5 + 32 : value
  return convertedValue.toFixed(1)
}

export const convertSetpoint = (value: number, unit: TemperatureUnit) => {
  if (typeof value !== 'number') {
    return ''
  }
  const convertedValue = unit === 'Imperial' ? Math.round(((value * 9) / 5 + 32) * 2) / 2 : value
  return convertedValue.toFixed(1)
}

export const joinStrings = (...strs: string[]) => strs.filter((x) => !!x).join('\n')

const DYNAMIC_TEMP_MP_COL_PREFIX = '$$tempmp$$'
const DYNAMIC_DOOR_MP_COL_PREFIX = '$$doormp$$'

export const isDynamicTempMpCol = (columnName: string) => columnName.startsWith(DYNAMIC_TEMP_MP_COL_PREFIX)

export const getDynamicTempMpColName = (mpName: string) => `${DYNAMIC_TEMP_MP_COL_PREFIX}${mpName}`

export const isDynamicDoorMpCol = (columnName: string) => columnName.startsWith(DYNAMIC_DOOR_MP_COL_PREFIX)

export const getDynamicDoorMpColName = (mpName: string) => `${DYNAMIC_DOOR_MP_COL_PREFIX}${mpName}`

export const REEFER_INTERFACE_TYPE = {
  ReeferInterface: 1,
  NoReeferInterface: 2,
  NoReefer: 3,
}

export const isVirtualsetPointEnabledForAsset = (virtualSetpointAccessTypeId: number, reeferInterfaceType: number) => {
  // NO_REEFER_INTERFACE_ONLY AND REEFER_INTERFACE_TYPE.NoReeferInterface -> VSP enabled for the asset
  // REEFER_AND_REEFER_INTERFACE AND REEFER_INTERFACE_TYPE.NoReeferInterface -> VSP enabled for the asset
  // REEFER_AND_REEFER_INTERFACE AND REEFER_INTERFACE_TYPE.ReeferInterface -> VSP enabled for the asset
  // DISABLED -> VSP disabled for the asset
  switch (virtualSetpointAccessTypeId) {
    case VSP_ACCESS_TYPE.NoReeferInterfaceOnly:
      return reeferInterfaceType === REEFER_INTERFACE_TYPE.NoReeferInterface
    case VSP_ACCESS_TYPE.ReeferAndReeferInterface:
      return reeferInterfaceType !== REEFER_INTERFACE_TYPE.NoReefer
    default:
      return false
  }
}

export const estimateColumnWidth = (text: string): number => {
  const words = text.split(' ')
  const maxChars = Math.max(...words.map((w) => w.length))
  return Math.max(maxChars * 10, 120)
}

export const getLocationColMeta = (gatewayOn: boolean, locationSampleTime: Date, value: string | null) => {
  const isValueBlank = isNullOrUndefined(value) || isEmpty(value.trim())
  const addressAgeMinutes = DateTime.now().diff(DateTime.fromJSDate(new Date(locationSampleTime)), 'minutes').minutes
  return {
    isValueBlank,
    displayBlank: !gatewayOn || isValueBlank,
    displayMissing: addressAgeMinutes > 90,
  }
}

export const getFridgePropColMeta = (powerState: string, fridgeOnline: boolean, value: string | number | null) => {
  const isDataOnline = powerState === 'Reefer On'
  const isValueBlank = isNullOrUndefined(value) || value === ''
  return {
    isValueBlank,
    displayBlank: !isDataOnline || isValueBlank,
    displayMissing: isDataOnline && !fridgeOnline,
  }
}

export const getBasicPropColMeta = (gatewayOn: boolean, moduleOn: boolean, value: string | number | null) => {
  const isValueBlank = isNullOrUndefined(value) || value === ''
  return {
    isValueBlank,
    displayBlank: !gatewayOn || isValueBlank,
    displayMissing: gatewayOn && !moduleOn,
  }
}

export const getVspColMeta = (
  virtualSetpointAccessTypeId: number | undefined,
  reeferInterfaceTypeId: number,
  value: string | number | null
) => {
  const isValueBlank = isNullOrUndefined(value)
  const displayBlank =
    isNullOrUndefined(value) ||
    isNullOrUndefined(virtualSetpointAccessTypeId) ||
    !isVirtualsetPointEnabledForAsset(virtualSetpointAccessTypeId, reeferInterfaceTypeId)

  return {
    isValueBlank,
    displayBlank,
    displayMissing: false,
  }
}

export const getTempMpColMeta = (gatewayOn: boolean, tagOnline: boolean, value: number) => {
  const isValueBlank = isNullOrUndefined(value)
  return {
    isValueBlank,
    displayBlank: !gatewayOn || isValueBlank,
    displayMissing: gatewayOn && !tagOnline,
  }
}

export const getDoorMpColMeta = (gatewayOn: boolean, tagOnline: boolean, value: string) => {
  const isValueBlank = isNullOrUndefined(value) || value === ''
  return {
    isValueBlank,
    displayBlank: !gatewayOn || isValueBlank,
    displayMissing: gatewayOn && !tagOnline,
  }
}
